import { Box, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { MouseEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { CardHeaderFlex } from '../../../component/cardHeaderFlex/CardHeaderFlex';
import Skeleton from '../../../component/skeleton/Skeleton';
import { useMarketTerm } from '../../../hooks/useMarketTerm';
import { UtilizationPointType } from '../../../redux/slices/tools/toolsInterface';
import { CardStyled } from '../../../styled/components';
import { ACTIVITY_COLORS } from './activityColors';

type UtilizationMixCardProps = {
  points: UtilizationPointType[];
  /** The page's window, so the trend covers the same stretch and no more. */
  from?: string;
  to?: string;
  fetching: boolean;
  /** Smallest height for the trend plot; it grows to fill the card. */
  height?: number;
};

// On a job site, and everything else.
const MIX_COLORS = [ACTIVITY_COLORS.out, '#6B7280'];
const AXIS = { left: 34, bottom: 18, top: 8, right: 8 };

/**
 * Where the fleet stands, and how the on-site share has moved over the window.
 * The movement charts say how much went out and came back; this says how much
 * of the fleet that left sitting idle.
 */
export const UtilizationMixCard = ({ points, from, to, fetching, height = 150 }: UtilizationMixCardProps): ReactElement => {
  const term = useMarketTerm();
  const theme = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: height });
  const [hover, setHover] = useState<number | undefined>();

  const windowPoints = useMemo(() => {
    const start = from ? DateTime.fromISO(from).toISODate() : undefined;
    // `to` is exclusive, matching every other window on these pages.
    const end = to ? DateTime.fromISO(to).toISODate() : undefined;
    if (!start && !end) return points;
    return points.filter((point) => (!start || point.date >= start) && (!end || point.date < end));
  }, [points, from, to]);

  // The plot is drawn in real pixels, so it has to know the box it was given.
  useEffect(() => {
    const node = plotRef.current;
    if (!node) return undefined;
    const measure = () => {
      const box = node.getBoundingClientRect();
      setSize({ w: box.width, h: Math.max(height, box.height) });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetching, windowPoints.length, height]);

  const latest = windowPoints.length ? windowPoints[windowPoints.length - 1] : undefined;
  // Two buckets, not three. The history's in-transit and warehouse split is
  // decided by which scan event an item last saw, and equipment simply back at
  // the yard rarely gets a storing scan -- so a three-way split reads as
  // "nothing is at home", which is not what is happening. On a job site versus
  // not is the distinction the data supports, and the one the line below plots.
  const parts = latest
    ? [
        { label: 'On a job site', value: latest.onsite },
        { label: term('Not Tracked'), value: Math.max(0, latest.totalEquipment - latest.onsite) },
      ]
    : [];
  const total = parts.reduce((sum, part) => sum + part.value, 0);

  const inner = Math.max(0, size.w - AXIS.left - AXIS.right);
  const plotHeight = Math.max(0, size.h - AXIS.top - AXIS.bottom);
  const x = (index: number) => AXIS.left + (inner / Math.max(1, windowPoints.length - 1)) * index;
  const y = (pct: number) => AXIS.top + plotHeight - (Math.min(Math.max(pct, 0), 100) / 100) * plotHeight;
  const line = windowPoints.map((point, index) => `${x(index)},${y(point.trackedPct)}`).join(' ');
  const hovered = hover !== undefined ? windowPoints[hover] : undefined;

  const onMove = (event: MouseEvent<HTMLDivElement>) => {
    const box = plotRef.current?.getBoundingClientRect();
    if (!box || windowPoints.length < 2) return;
    const ratio = (event.clientX - box.left - AXIS.left) / Math.max(1, inner);
    setHover(Math.min(windowPoints.length - 1, Math.max(0, Math.round(ratio * (windowPoints.length - 1)))));
  };

  return (
    <CardStyled sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeaderFlex title="Utilization" />
      <CardContent sx={{ pt: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}>
        {fetching ? (
          <Skeleton fetching height={height + 60} />
        ) : !total ? (
          <Typography variant="caption" color="text.secondary">
            No equipment history yet
          </Typography>
        ) : (
          <>
            <Box sx={{ width: '100%', height: 12, display: 'flex', gap: '2px', mb: 1 }}>
              {parts.map((part, index) =>
                part.value ? (
                  <Box
                    key={part.label}
                    sx={{ width: `${(part.value / total) * 100}%`, bgcolor: MIX_COLORS[index], borderRadius: '2px' }}
                    title={`${part.label}: ${part.value}`}
                  />
                ) : null,
              )}
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {parts.map((part, index) => (
                <Stack key={part.label} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: MIX_COLORS[index] }} />
                  <Typography variant="caption" color="text.secondary">
                    {part.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {part.value.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round((part.value / total) * 100)}%
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              On-site share over this window
            </Typography>
            {/* flexGrow: the plot takes whatever height the card has left, so
                the chart is most of the box rather than a strip at the bottom. */}
            <Box ref={plotRef} sx={{ position: 'relative', flexGrow: 1, minHeight: height }} onMouseMove={onMove} onMouseLeave={() => setHover(undefined)}>
              {size.w > 0 && windowPoints.length > 1 && (
                <svg width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`} style={{ display: 'block', overflow: 'visible' }}>
                  {[0, 25, 50, 75, 100].map((tick) => (
                    <g key={tick}>
                      <line
                        x1={AXIS.left}
                        x2={size.w - AXIS.right}
                        y1={y(tick)}
                        y2={y(tick)}
                        stroke={tick === 0 ? theme.palette.grey[400] : theme.palette.grey[200]}
                        strokeWidth={1}
                        shapeRendering="crispEdges"
                      />
                      {tick % 25 === 0 && (
                        <text
                          x={AXIS.left - 7}
                          y={y(tick) + 3.5}
                          textAnchor="end"
                          fill={theme.palette.text.secondary}
                          fontSize={10}
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {`${tick}%`}
                        </text>
                      )}
                    </g>
                  ))}
                  <polygon
                    points={`${x(0)},${AXIS.top + plotHeight} ${line} ${x(windowPoints.length - 1)},${AXIS.top + plotHeight}`}
                    fill={ACTIVITY_COLORS.out}
                    fillOpacity={0.1}
                  />
                  <polyline points={line} fill="none" stroke={ACTIVITY_COLORS.out} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {hover !== undefined && hovered && (
                    <g pointerEvents="none">
                      <line x1={x(hover)} x2={x(hover)} y1={AXIS.top} y2={AXIS.top + plotHeight} stroke={theme.palette.grey[400]} strokeWidth={1} />
                      <circle
                        cx={x(hover)}
                        cy={y(hovered.trackedPct)}
                        r={4}
                        fill={ACTIVITY_COLORS.out}
                        stroke={theme.palette.background.paper}
                        strokeWidth={2}
                      />
                    </g>
                  )}
                  <text x={AXIS.left} y={size.h - 4} fill={theme.palette.text.secondary} fontSize={10}>
                    {DateTime.fromISO(windowPoints[0].date).toFormat('MMM d')}
                  </text>
                  <text x={size.w - AXIS.right} y={size.h - 4} textAnchor="end" fill={theme.palette.text.secondary} fontSize={10}>
                    {DateTime.fromISO(windowPoints[windowPoints.length - 1].date).toFormat('MMM d')}
                  </text>
                </svg>
              )}
              {hovered && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: Math.min(Math.max(x(hover as number) - 60, 0), Math.max(0, size.w - 120)),
                    top: 0,
                    pointerEvents: 'none',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 1,
                    boxShadow: 2,
                    px: 1,
                    py: 0.5,
                    zIndex: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {DateTime.fromISO(hovered.date).toFormat('MMM d, yyyy')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {`${Math.round(hovered.trackedPct)}% on site · ${hovered.onsite.toLocaleString()} of ${hovered.totalEquipment.toLocaleString()}`}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </CardStyled>
  );
};

export default UtilizationMixCard;
